import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';

import { getPublicDiscoveryFeed } from '../../Services/demandLifecycle';
import GoogleMap from '../../Components/Geo/GoogleMap';
import FirebaseImage from '../../Components/Media/FirebaseImage';
import TrustPassport from '../../Components/Trust/TrustPassport';
import { distanceKm, getCurrentPosition, toCoordinate } from '../../Utils/geo';
import { useI18n } from '../../i18n/I18nProvider';

const TYPE_LABELS = {
  window: 'Menuiserie / miroiterie',
  electricity: 'Électricité',
};

const ACCESS_CONTENT = {
  profile: {
    eyebrow: 'Profil artisan',
    title: 'Le bon artisan mérite plus qu’un simple aperçu.',
    description: 'Créez gratuitement votre espace client pour consulter les réalisations, les avis vérifiés et les informations utiles avant de prendre contact.',
    recommendedRole: 'Client',
    benefits: [
      'Profil, spécialités et réalisations détaillées',
      'Avis vérifiés et signaux de confiance',
      'Prise de contact et suivi dans un espace sécurisé',
    ],
  },
  contact: {
    eyebrow: 'Contact sécurisé',
    title: 'Échangez sans exposer vos coordonnées personnelles.',
    description: 'La prise de contact se fait depuis un compte client afin de garder les messages, estimations et décisions rattachés à votre projet.',
    recommendedRole: 'Client',
    benefits: [
      'Messagerie liée à votre demande',
      'Estimations faciles à comparer',
      'Historique accessible à tout moment',
    ],
  },
  demand: {
    eyebrow: 'Mission disponible',
    title: 'Cette demande pourrait devenir votre prochain chantier.',
    description: 'Les détails complets sont réservés aux artisans inscrits pour protéger le client. Votre espace professionnel vous permet de consulter les informations utiles et d’envoyer une estimation.',
    recommendedRole: 'Artisan',
    benefits: [
      'Description et photos utiles à l’estimation',
      'Zone d’intervention et informations du projet',
      'Envoi et suivi de vos propositions',
    ],
  },
};

const ACCESS_CONTENT_EN = {
  profile: {
    eyebrow: 'Artisan profile',
    title: 'The right artisan deserves more than a quick preview.',
    description: 'Create your free client space to review completed work, verified reviews and useful information before making contact.',
    benefits: [
      'Detailed profile, specialities and completed work',
      'Verified reviews and trust signals',
      'Contact and follow-up in a secure space',
    ],
  },
  contact: {
    eyebrow: 'Secure contact',
    title: 'Talk without exposing your personal contact details.',
    description: 'Contact takes place from a client account so messages, estimates and decisions stay connected to your project.',
    benefits: [
      'Messaging linked to your request',
      'Estimates that are easy to compare',
      'History available at any time',
    ],
  },
  demand: {
    eyebrow: 'Available job',
    title: 'This request could become your next job.',
    description: 'Full details are reserved for registered artisans to protect the client. Your professional space lets you review the useful information and send an estimate.',
    benefits: [
      'Description and photos useful for estimating',
      'Service area and project information',
      'Send and track your proposals',
    ],
  },
};

const ROLE_DETAILS = {
  Client: {
    path: '/signup',
    label: 'Créer mon compte client',
    shortLabel: 'Compte client',
    description: 'Publier un besoin, comparer les artisans et suivre votre projet.',
    icon: PersonOutlineRoundedIcon,
  },
  Artisan: {
    path: '/registerartisan',
    label: 'Créer mon compte artisan',
    shortLabel: 'Compte artisan',
    description: 'Découvrir des missions, envoyer des estimations et développer votre activité.',
    icon: HandymanOutlinedIcon,
  },
};

const ROLE_DETAILS_EN = {
  Client: {
    label: 'Create my client account',
    shortLabel: 'Client account',
    description: 'Post a request, compare artisans and follow your project.',
  },
  Artisan: {
    label: 'Create my artisan account',
    shortLabel: 'Artisan account',
    description: 'Discover jobs, send estimates and grow your activity.',
  },
};

const formatPublicDate = (value, locale = 'fr-FR', fallback = 'Récemment') => {
  if (!value) return fallback;
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

const PublicDiscovery = ({ emailVerified, loggedIn, userType }) => {
  const history = useHistory();
  const { pick, locale } = useI18n();
  const [artisans, setArtisans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [publicDemands, setPublicDemands] = useState([]);
  const [feedStats, setFeedStats] = useState(null);
  const [position, setPosition] = useState(null);
  const [activeId, setActiveId] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [dataError, setDataError] = useState('');
  const [accessGate, setAccessGate] = useState(null);

  useEffect(() => {
    let active = true;
    setDataError('');

    getPublicDiscoveryFeed()
      .then((feed) => {
        if (!active) return;
        setArtisans(feed.artisans);
        setProjects(feed.projects);
        setPublicDemands(feed.demands);
        setFeedStats(feed.stats || null);
      })
      .catch((error) => {
        if (!active) return;
        console.error('Public discovery feed unavailable', error);
        setDataError(
          error?.message && !/^internal$/i.test(error.message)
            ? error.message
            : pick('Les données publiques sont temporairement indisponibles.', 'Public data is temporarily unavailable.'),
        );
      });

    return () => { active = false; };
  }, [pick]);

  useEffect(() => {
    if (!accessGate) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setAccessGate(null);
    };

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [accessGate]);

  const locate = async () => {
    setLoadingLocation(true);
    try {
      setPosition(await getCurrentPosition({ enableHighAccuracy: false }));
    } finally {
      setLoadingLocation(false);
    }
  };

  const rankedArtisans = useMemo(() => artisans
    .map((artisan) => ({
      ...artisan,
      distance: position ? distanceKm(position, artisan.public_location) : null,
    }))
    .sort((a, b) => {
      if (Number.isFinite(a.distance) && Number.isFinite(b.distance)) return a.distance - b.distance;
      return Number(b.trust_score || 0) - Number(a.trust_score || 0);
    }), [artisans, position]);

  const rankedDemands = useMemo(() => publicDemands
    .map((demand) => ({
      ...demand,
      distance: position ? distanceKm(position, demand.public_location) : null,
    }))
    .sort((a, b) => {
      if (Number.isFinite(a.distance) && Number.isFinite(b.distance)) return a.distance - b.distance;
      return 0;
    }), [position, publicDemands]);

  const markers = useMemo(() => [
    ...rankedArtisans.map((artisan) => ({
      id: `artisan-${artisan.id}`,
      position: artisan.public_location,
      label: artisan.display_name || pick('Artisan vérifié', 'Verified artisan'),
      kind: 'artisan',
    })),
    ...rankedDemands.map((demand) => ({
      id: `demand-${demand.id}`,
      position: demand.public_location,
      label: demand.demand_type === 'electricity' ? pick('Électricité', 'Electrical work') : demand.demand_type === 'window' ? pick('Menuiserie / miroiterie', 'Joinery / glazing') : pick('Demande active', 'Active request'),
      kind: 'demand',
    })),
    ...projects.map((project) => ({
      id: `project-${project.id}`,
      position: project.public_location,
      label: project.title || pick('Projet réalisé', 'Completed project'),
      kind: 'project',
    })),
  ].filter((item) => toCoordinate(item.position)), [pick, projects, rankedArtisans, rankedDemands]);

  const mapCenter = position
    || rankedDemands[0]?.public_location
    || rankedArtisans[0]?.public_location
    || projects[0]?.public_location;

  const openMemberArea = (kind, item = null) => {
    if (!loggedIn) {
      setAccessGate({ kind, item });
      return;
    }

    if (!emailVerified) {
      setAccessGate({ kind: 'verify', item });
      return;
    }

    if (kind === 'profile' || kind === 'contact') {
      history.push('/artisanboutique', { artisanId: item?.id });
      return;
    }

    if (kind === 'demand' && userType === 'Artisan') {
      history.push('/newjobs', { highlightedDemandId: item?.id });
      return;
    }

    if (kind === 'demand' && userType === 'Client') {
      setAccessGate({ kind: 'client-demand', item });
    }
  };

  const goToRegistration = (role) => {
    const roleDetails = ROLE_DETAILS[role];
    history.push(roleDetails.path, {
      from: '/discover',
      intent: accessGate?.kind,
      itemId: accessGate?.item?.id,
    });
  };

  const activateCardFromKeyboard = (event, action) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action();
  };

  const openPublicProject = (project) => {
    if (!project?.artisan_id) return;
    openMemberArea('profile', { ...project, id: project.artisan_id });
  };

  const gateTemplate = accessGate && ACCESS_CONTENT[accessGate.kind];
  const gateTemplateEn = accessGate && ACCESS_CONTENT_EN[accessGate.kind];
  const gateContent = gateTemplate ? {
    ...gateTemplate,
    eyebrow: pick(gateTemplate.eyebrow, gateTemplateEn?.eyebrow || gateTemplate.eyebrow),
    title: pick(gateTemplate.title, gateTemplateEn?.title || gateTemplate.title),
    description: pick(gateTemplate.description, gateTemplateEn?.description || gateTemplate.description),
    benefits: gateTemplate.benefits.map((benefit, index) => pick(benefit, gateTemplateEn?.benefits?.[index] || benefit)),
  } : null;
  const recommendedRole = gateContent?.recommendedRole || 'Client';
  const alternativeRole = recommendedRole === 'Client' ? 'Artisan' : 'Client';
  const RecommendedIcon = ROLE_DETAILS[recommendedRole].icon;
  const AlternativeIcon = ROLE_DETAILS[alternativeRole].icon;
  const stats = {
    activeDemands: Math.max(Number(feedStats?.active_demands || 0), publicDemands.length),
    publicArtisans: Math.max(Number(feedStats?.public_artisans || 0), artisans.length),
    verifiedProjects: Math.max(Number(feedStats?.verified_projects || 0), projects.length),
    trustedArtisans: Math.max(
      Number(feedStats?.trusted_artisans || 0),
      rankedArtisans.filter((item) => Number(item.trust_score) >= 80).length,
    ),
  };

  return (
    <Page>
      <Hero>
        <HeroCopy>
          <Eyebrow>{pick('Autour de vous', 'Around you')}</Eyebrow>
          <h1>{pick('Découvrez les demandes, artisans et travaux vérifiés avant de vous inscrire.', 'Explore requests, artisans and verified work before you sign up.')}</h1>
          <p>{pick('Explorez librement les aperçus publics. Les informations sensibles et la prise de contact sont accessibles depuis un espace membre sécurisé.', 'Explore public previews freely. Sensitive information and contact options are available from a secure member space.')}</p>
          <HeroTrust>
            <span><CheckCircleRoundedIcon /> {pick('Inscription gratuite', 'Free sign-up')}</span>
            <span><CheckCircleRoundedIcon /> {pick('Deux espaces adaptés', 'Two purpose-built spaces')}</span>
            <span><CheckCircleRoundedIcon /> {pick('Données personnelles protégées', 'Personal data protected')}</span>
          </HeroTrust>
        </HeroCopy>

        <HeroPanel>
          <HeroPanelLabel>{pick('Que souhaitez-vous faire ?', 'What would you like to do?')}</HeroPanelLabel>
          <HeroRoleButton type="button" onClick={() => history.push('/signup')}>
            <PersonOutlineRoundedIcon />
            <span><strong>{pick('J’ai un projet', 'I have a project')}</strong><small>{pick('Créer un compte client', 'Create a client account')}</small></span>
            <ArrowForwardRoundedIcon />
          </HeroRoleButton>
          <HeroRoleButton type="button" onClick={() => history.push('/registerartisan')}>
            <HandymanOutlinedIcon />
            <span><strong>{pick('Je cherche des missions', 'I am looking for jobs')}</strong><small>{pick('Créer un compte artisan', 'Create an artisan account')}</small></span>
            <ArrowForwardRoundedIcon />
          </HeroRoleButton>
          <ExistingAccount to="/login"><LoginRoundedIcon /> {pick('Déjà membre ? Se connecter', 'Already a member? Sign in')}</ExistingAccount>
          <LocationButton type="button" onClick={locate} disabled={loadingLocation}>
            <MyLocationOutlinedIcon /> {loadingLocation ? pick('Localisation…', 'Locating…') : pick('Afficher les résultats proches de moi', 'Show results near me')}
          </LocationButton>
        </HeroPanel>
      </Hero>

      {dataError && <DataNotice role="status">{dataError}</DataNotice>}

      <Stats>
        <div><strong>{stats.activeDemands}</strong><span>{pick('demandes actives publiques', 'public active requests')}</span></div>
        <div><strong>{stats.publicArtisans}</strong><span>{pick('artisans disponibles', 'available artisans')}</span></div>
        <div><strong>{stats.verifiedProjects}</strong><span>{pick('projets vérifiés récents', 'recent verified projects')}</span></div>
        <div><strong>{stats.trustedArtisans}</strong><span>{pick('profils très fiables', 'high-trust profiles')}</span></div>
      </Stats>

      {!loggedIn && (
        <AccessNotice>
          <AccessNoticeIcon><LockOutlinedIcon /></AccessNoticeIcon>
          <div>
            <strong>{pick('Vous voyez actuellement un aperçu public.', 'You are currently viewing a public preview.')}</strong>
            <span>{pick('Créez votre espace gratuit uniquement lorsque vous souhaitez consulter les détails, contacter un artisan ou répondre à une demande.', 'Create your free space only when you want to see details, contact an artisan or respond to a request.')}</span>
          </div>
          <AccessNoticeLinks>
            <button type="button" onClick={() => setAccessGate({ kind: 'profile' })}>{pick('Découvrir l’accès client', 'Explore client access')}</button>
            <button type="button" onClick={() => setAccessGate({ kind: 'demand' })}>{pick('Découvrir l’accès artisan', 'Explore artisan access')}</button>
          </AccessNoticeLinks>
        </AccessNotice>
      )}

      <DiscoveryGrid>
        <MapCard>
          <GoogleMap
            center={mapCenter}
            markers={markers}
            activeId={activeId}
            onMarkerClick={(item) => setActiveId(item.id)}
            zoom={9}
            ariaLabel={pick('Carte publique des demandes, artisans et projets vérifiés', 'Public map of requests, artisans and verified projects')}
          />
          <MapLegend aria-label={pick('Légende de la carte', 'Map legend')}>
            <LegendItem $kind="demand"><i>D</i><span>{pick('Demande', 'Request')}</span></LegendItem>
            <LegendItem $kind="artisan"><i>A</i><span>{pick('Artisan', 'Artisan')}</span></LegendItem>
            <LegendItem $kind="project"><i>✓</i><span>{pick('Projet vérifié', 'Verified project')}</span></LegendItem>
          </MapLegend>
          <MapPrivacyNote><LockOutlinedIcon /> {pick('Positions volontairement approximatives', 'Locations intentionally approximate')}</MapPrivacyNote>
        </MapCard>

        <SideContent>
          <List>
            <SectionHeading>
              <div><SectionTitle>{pick('Demandes actives', 'Active requests')}</SectionTitle><SectionSubtitle>{pick('Des opportunités visibles sans révéler les informations privées du client.', 'Opportunities you can explore without exposing clients’ private information.')}</SectionSubtitle></div>
              <SectionPill>{pick('Pour les artisans', 'For artisans')}</SectionPill>
            </SectionHeading>
            {rankedDemands.length === 0 ? (
              <Empty>
                <HomeRepairServiceOutlinedIcon />
                {pick('Les demandes apparaissent ici lorsque le client autorise un aperçu public anonymisé.', 'Requests appear here when the client allows an anonymised public preview.')}
              </Empty>
            ) : rankedDemands.slice(0, 6).map((demand) => (
              <DemandCard
                key={demand.id}
                $interactive
                role="button"
                tabIndex={0}
                onMouseEnter={() => setActiveId(`demand-${demand.id}`)}
                onFocus={() => setActiveId(`demand-${demand.id}`)}
                onClick={() => openMemberArea('demand', demand)}
                onKeyDown={(event) => activateCardFromKeyboard(event, () => openMemberArea('demand', demand))}
                aria-label={pick('Ouvrir la demande', 'Open request')}
              >
                <DemandMedia>
                  <FirebaseImage
                    src={demand.cover_image}
                    fallbackSrc="/images/project-placeholder.svg"
                    alt={pick('Aperçu anonymisé d’une demande', 'Anonymised request preview')}
                  />
                  <PreviewBadge><LockOutlinedIcon /> {pick('Aperçu public', 'Public preview')}</PreviewBadge>
                </DemandMedia>
                <DemandBody>
                  <DemandTop>
                    <span>{pick(TYPE_LABELS[demand.demand_type] || 'Travaux', demand.demand_type === 'electricity' ? 'Electrical work' : demand.demand_type === 'window' ? 'Joinery / glazing' : 'Work')}</span>
                    <small><CalendarTodayOutlinedIcon /> {formatPublicDate(demand.published_at, locale, pick('Récemment', 'Recently'))}</small>
                  </DemandTop>
                  <p className="project-description">{demand.summary || pick('Une nouvelle demande est disponible dans cette zone.', 'A new request is available in this area.')}</p>
                  <LocationLine>
                    <LocationOnOutlinedIcon />
                    {demand.city || demand.region || pick('Zone approximative', 'Approximate area')}
                    {Number.isFinite(demand.distance) ? ` · ${demand.distance.toFixed(1)} km` : ''}
                  </LocationLine>
                  <PrimaryCardAction type="button" onClick={(event) => { event.stopPropagation(); openMemberArea('demand', demand); }}>
                    {!loggedIn && <LockOutlinedIcon />}
                    {loggedIn && userType === 'Artisan' ? pick('Ouvrir mes missions', 'Open my jobs') : pick('Voir la demande complète', 'View full request')}
                    <ArrowForwardRoundedIcon />
                  </PrimaryCardAction>
                </DemandBody>
              </DemandCard>
            ))}
          </List>

          <List>
            <SectionHeading>
              <div><SectionTitle>{pick('Artisans recommandés', 'Recommended artisans')}</SectionTitle><SectionSubtitle>{pick('Comparez les profils et choisissez avec plus de sérénité.', 'Compare profiles and choose with confidence.')}</SectionSubtitle></div>
              <SectionPill>{pick('Pour les clients', 'For clients')}</SectionPill>
            </SectionHeading>
            {rankedArtisans.length === 0 ? <Empty>{pick('Aucun profil public n’est encore disponible dans cette zone.', 'No public profile is available in this area yet.')}</Empty> : rankedArtisans.slice(0, 5).map((artisan) => (
              <ArtisanCard
                key={artisan.id}
                $interactive
                role="button"
                tabIndex={0}
                onMouseEnter={() => setActiveId(`artisan-${artisan.id}`)}
                onFocus={() => setActiveId(`artisan-${artisan.id}`)}
                onClick={() => openMemberArea('profile', artisan)}
                onKeyDown={(event) => activateCardFromKeyboard(event, () => openMemberArea('profile', artisan))}
                aria-label={pick(`Ouvrir le profil de ${artisan.display_name || 'cet artisan'}`, `Open ${artisan.display_name || 'artisan'} profile`)}
              >
                <Identity>
                  <FirebaseImage src={artisan.profile_image} fallbackSrc="/images/user.svg" alt={pick('Photo de profil artisan', 'Artisan profile photo')} />
                  <div>
                    <strong>{artisan.display_name || pick('Artisan vérifié', 'Verified artisan')}</strong>
                    <span><LocationOnOutlinedIcon /> {artisan.city || artisan.region || pick('Zone locale', 'Local area')}{Number.isFinite(artisan.distance) ? ` · ${artisan.distance.toFixed(1)} km` : ''}</span>
                  </div>
                </Identity>
                <TrustPassport profile={artisan} compact />
                <CardActions>
                  <PrimaryCardAction type="button" onClick={(event) => { event.stopPropagation(); openMemberArea('profile', artisan); }}>
                    {!loggedIn && <LockOutlinedIcon />} {pick('Voir le profil complet', 'View full profile')} <ArrowForwardRoundedIcon />
                  </PrimaryCardAction>
                  <SecondaryCardAction type="button" onClick={(event) => { event.stopPropagation(); openMemberArea('contact', artisan); }}>
                    {pick('Contacter', 'Contact')}
                  </SecondaryCardAction>
                </CardActions>
              </ArtisanCard>
            ))}
          </List>
        </SideContent>
      </DiscoveryGrid>

      <ProjectsSection>
        <SectionHeading>
          <div><SectionTitle>{pick('Travaux réalisés et vérifiés', 'Completed and verified work')}</SectionTitle><SectionSubtitle>{pick('Des réalisations publiées avec l’accord des membres après la fin du projet.', 'Projects published with members’ consent after completion.')}</SectionSubtitle></div>
          <SectionPill>{pick('Preuves de savoir-faire', 'Proof of craftsmanship')}</SectionPill>
        </SectionHeading>
        <ProjectGrid>
          {projects.slice(0, 9).map((project) => (
            <ProjectCard
              key={project.id}
              $interactive={Boolean(project.artisan_id)}
              role={project.artisan_id ? 'button' : undefined}
              tabIndex={project.artisan_id ? 0 : undefined}
              onMouseEnter={() => setActiveId(`project-${project.id}`)}
              onFocus={() => setActiveId(`project-${project.id}`)}
              onClick={() => openPublicProject(project)}
              onKeyDown={(event) => project.artisan_id && activateCardFromKeyboard(event, () => openPublicProject(project))}
              aria-label={project.artisan_id ? pick('Voir le profil de l’artisan de ce projet', 'View the artisan profile for this project') : undefined}
            >
              <FirebaseImage src={project.cover_image} fallbackSrc="/images/project-placeholder.svg" alt={project.title || pick('Projet réalisé', 'Completed project')} />
              <div>
                <span><VerifiedOutlinedIcon /> {pick('Projet vérifié', 'Verified project')}</span>
                <h3 className="user-content">{project.title || pick('Projet réalisé', 'Completed project')}</h3>
                <p>{project.city || project.region || pick('Localisation approximative', 'Approximate location')}</p>
              </div>
            </ProjectCard>
          ))}
          {projects.length === 0 && <Empty><ConstructionOutlinedIcon /> {pick('Les premiers projets publics apparaîtront ici après paiement, vérification et autorisation de publication.', 'Public projects will appear here after payment, verification and publication consent.')}</Empty>}
        </ProjectGrid>
      </ProjectsSection>

      {!loggedIn && (
        <FinalCta>
          <div>
            <Eyebrow>{pick('Prêt à passer à l’étape suivante ?', 'Ready for the next step?')}</Eyebrow>
            <h2>{pick('Choisissez l’espace qui correspond réellement à votre besoin.', 'Choose the space that actually matches your needs.')}</h2>
            <p>{pick('Pas de parcours générique : clients et artisans disposent chacun de leurs propres outils.', 'No generic workflow: clients and artisans each get tools designed for their role.')}</p>
          </div>
          <FinalCtaActions>
            <button type="button" onClick={() => goToRegistration('Client')}><PersonOutlineRoundedIcon /> {pick('Créer un compte client', 'Create a client account')}</button>
            <button type="button" onClick={() => goToRegistration('Artisan')}><HandymanOutlinedIcon /> {pick('Créer un compte artisan', 'Create an artisan account')}</button>
            <Link to="/login">{pick('J’ai déjà un compte', 'I already have an account')}</Link>
          </FinalCtaActions>
        </FinalCta>
      )}

      {accessGate && gateContent && (
        <GateBackdrop role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setAccessGate(null);
        }}>
          <GateDialog role="dialog" aria-modal="true" aria-labelledby="access-gate-title">
            <GateTop>
              <GateIcon><LockOutlinedIcon /></GateIcon>
              <CloseButton type="button" aria-label={pick('Fermer', 'Close')} onClick={() => setAccessGate(null)}><CloseRoundedIcon /></CloseButton>
            </GateTop>

            <GateEyebrow>{gateContent.eyebrow}</GateEyebrow>
            <h2 id="access-gate-title">{gateContent.title}</h2>
            <GateDescription>{gateContent.description}</GateDescription>

            <BenefitList>
              {gateContent.benefits.map((benefit) => <li key={benefit}><CheckCircleRoundedIcon /> {benefit}</li>)}
            </BenefitList>

            <RecommendedRole type="button" onClick={() => goToRegistration(recommendedRole)}>
              <RoleIcon><RecommendedIcon /></RoleIcon>
              <span>
                <small>{pick('Recommandé pour continuer', 'Recommended to continue')}</small>
                <strong>{pick(ROLE_DETAILS[recommendedRole].label, ROLE_DETAILS_EN[recommendedRole].label)}</strong>
                <em>{pick(ROLE_DETAILS[recommendedRole].description, ROLE_DETAILS_EN[recommendedRole].description)}</em>
              </span>
              <ArrowForwardRoundedIcon />
            </RecommendedRole>

            <AlternativeRole type="button" onClick={() => goToRegistration(alternativeRole)}>
              <AlternativeIcon />
              <span><strong>{pick(ROLE_DETAILS[alternativeRole].shortLabel, ROLE_DETAILS_EN[alternativeRole].shortLabel)}</strong><small>{pick(ROLE_DETAILS[alternativeRole].description, ROLE_DETAILS_EN[alternativeRole].description)}</small></span>
              <ArrowForwardRoundedIcon />
            </AlternativeRole>

            <GateLogin>
              <span>{pick('Vous avez déjà un compte ?', 'Already have an account?')}</span>
              <button type="button" onClick={() => history.push('/login', { from: '/discover' })}>{pick('Se connecter', 'Sign in')}</button>
            </GateLogin>
            <GatePrivacy><LockOutlinedIcon /> {pick('Vos coordonnées restent privées jusqu’à ce que vous choisissiez d’échanger.', 'Your contact details remain private until you choose to interact.')}</GatePrivacy>
          </GateDialog>
        </GateBackdrop>
      )}

      {accessGate?.kind === 'verify' && (
        <GateBackdrop role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setAccessGate(null);
        }}>
          <GateDialog role="dialog" aria-modal="true" aria-labelledby="verify-gate-title">
            <GateTop>
              <GateIcon><LockOutlinedIcon /></GateIcon>
              <CloseButton type="button" aria-label={pick('Fermer', 'Close')} onClick={() => setAccessGate(null)}><CloseRoundedIcon /></CloseButton>
            </GateTop>
            <GateEyebrow>{pick('Sécurité du compte', 'Account security')}</GateEyebrow>
            <h2 id="verify-gate-title">{pick('Une dernière étape avant d’accéder aux informations complètes.', 'One last step before accessing full information.')}</h2>
            <GateDescription>{pick('Vérifiez votre adresse e-mail pour protéger votre compte et pouvoir consulter les espaces réservés aux membres.', 'Verify your email address to protect your account and access member-only areas.')}</GateDescription>
            <RecommendedRole type="button" onClick={() => history.push('/verify-email')}>
              <RoleIcon><VerifiedOutlinedIcon /></RoleIcon>
              <span><small>{pick('Étape nécessaire', 'Required step')}</small><strong>{pick('Vérifier mon adresse e-mail', 'Verify my email address')}</strong><em>{pick('Vous reviendrez ensuite dans votre espace.', 'You will then return to your space.')}</em></span>
              <ArrowForwardRoundedIcon />
            </RecommendedRole>
            <GateLogin><button type="button" onClick={() => setAccessGate(null)}>{pick('Continuer à explorer les aperçus', 'Continue exploring previews')}</button></GateLogin>
          </GateDialog>
        </GateBackdrop>
      )}

      {accessGate?.kind === 'client-demand' && (
        <GateBackdrop role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setAccessGate(null);
        }}>
          <GateDialog role="dialog" aria-modal="true" aria-labelledby="client-demand-title">
            <GateTop>
              <GateIcon><HandymanOutlinedIcon /></GateIcon>
              <CloseButton type="button" aria-label={pick('Fermer', 'Close')} onClick={() => setAccessGate(null)}><CloseRoundedIcon /></CloseButton>
            </GateTop>
            <GateEyebrow>{pick('Espace professionnel', 'Professional space')}</GateEyebrow>
            <h2 id="client-demand-title">{pick('Les détails des missions sont réservés aux comptes artisans.', 'Job details are reserved for artisan accounts.')}</h2>
            <GateDescription>{pick('Votre compte client reste conçu pour publier vos propres demandes, comparer les propositions et suivre vos projets.', 'Your client account is designed to publish your own requests, compare proposals and follow your projects.')}</GateDescription>
            <RecommendedRole type="button" onClick={() => history.push('/demands')}>
              <RoleIcon><PersonOutlineRoundedIcon /></RoleIcon>
              <span><small>{pick('Votre espace actuel', 'Your current space')}</small><strong>{pick('Accéder à mes demandes', 'Go to my requests')}</strong><em>{pick('Publiez ou suivez un projet depuis votre tableau de bord.', 'Publish or follow a project from your dashboard.')}</em></span>
              <ArrowForwardRoundedIcon />
            </RecommendedRole>
            <GateLogin><button type="button" onClick={() => setAccessGate(null)}>{pick('Continuer à explorer', 'Continue exploring')}</button></GateLogin>
          </GateDialog>
        </GateBackdrop>
      )}
    </Page>
  );
};

const Page = styled.main`
  width: min(100% - 2rem, 82rem);
  margin: 0 auto;
  padding: clamp(1.2rem, 3vw, 2rem) 0 5rem;
`;

const Hero = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(18rem, .55fr);
  gap: clamp(1.2rem, 3vw, 2.4rem);
  align-items: stretch;
  padding: clamp(1.3rem, 4vw, 2.4rem);
  border: 1px solid var(--color-border);
  border-radius: 1.5rem;
  background:
    radial-gradient(circle at 82% 16%, rgba(192, 163, 117, .18), transparent 26%),
    #fff;
  box-shadow: var(--shadow-sm);

  @media (max-width: 62rem) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  align-self: center;

  h1 {
    max-width: 58rem;
    margin: 0;
    font-size: clamp(2rem, 5vw, 3.55rem);
    line-height: 1.02;
    letter-spacing: -.045em;
  }

  > p {
    max-width: 46rem;
    margin: .9rem 0 0;
    color: var(--color-textMuted);
    font-size: clamp(.92rem, 1.4vw, 1.06rem);
    line-height: 1.65;
  }
`;

const HeroTrust = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .55rem 1rem;
  margin-top: 1.25rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    color: #5f574d;
    font-size: .75rem;
    font-weight: 750;
  }

  svg {
    color: #8d6c36;
    font-size: 1rem;
  }
`;

const HeroPanel = styled.aside`
  display: grid;
  gap: .65rem;
  padding: 1rem;
  border: 1px solid #dfd8ce;
  border-radius: 1.2rem;
  background: rgba(250, 248, 244, .94);
  box-shadow: 0 .8rem 2.2rem rgba(29, 24, 19, .08);
`;

const HeroPanelLabel = styled.span`
  padding: .15rem .15rem .35rem;
  color: #5f574d;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .05em;
  text-transform: uppercase;
`;

const HeroRoleButton = styled.button`
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 1fr) auto;
  gap: .7rem;
  align-items: center;
  width: 100%;
  min-height: 4.2rem;
  padding: .72rem .78rem;
  border: 1px solid #ded7cd;
  border-radius: .95rem;
  background: #fff;
  color: #111;
  text-align: left;
  cursor: pointer;
  transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;

  > svg:first-child {
    width: 2.35rem;
    height: 2.35rem;
    padding: .55rem;
    border-radius: .7rem;
    background: #f1ece5;
  }

  > svg:last-child { font-size: 1.05rem; }

  span { display: grid; gap: .12rem; }
  strong { font-size: .84rem; }
  small { color: var(--color-textMuted); font-size: .68rem; }

  &:hover {
    transform: translateY(-1px);
    border-color: #b99a69;
    box-shadow: 0 .65rem 1.6rem rgba(29, 24, 19, .08);
  }
`;

const ExistingAccount = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .4rem;
  min-height: 2.75rem;
  border-radius: .85rem;
  background: #111;
  color: #fff;
  font-size: .76rem;
  font-weight: 850;
  text-decoration: none;

  svg { font-size: 1rem; }
`;

const Eyebrow = styled.span`
  display: block;
  margin-bottom: .55rem;
  color: #8d6c36;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .1em;
  text-transform: uppercase;
`;

const LocationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  min-height: 2.7rem;
  padding: .65rem .8rem;
  border: 0;
  border-radius: .8rem;
  background: transparent;
  color: #5c554d;
  font: inherit;
  font-size: .72rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: .55; cursor: wait; }
  svg { font-size: 1rem; }
`;

const DataNotice = styled.p`
  margin: 1rem 0 0;
  padding: .8rem 1rem;
  border: 1px solid #efcfcb;
  border-radius: .9rem;
  background: #fff6f5;
  color: #8c332c;
  font-size: .8rem;
`;

const Stats = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .75rem;
  margin: 1rem 0;

  div {
    display: grid;
    gap: .12rem;
    padding: 1rem;
    border: 1px solid var(--color-border);
    border-radius: 1rem;
    background: #fff;
  }

  strong { font-size: 1.55rem; }
  span { color: var(--color-textMuted); font-size: .72rem; }

  @media (max-width: 56rem) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 32rem) { grid-template-columns: 1fr; }
`;

const AccessNotice = styled.section`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: .85rem;
  align-items: center;
  margin: 0 0 1rem;
  padding: .9rem 1rem;
  border: 1px solid #ddcfb9;
  border-radius: 1rem;
  background: #fbf7f0;

  > div:nth-child(2) {
    display: grid;
    gap: .15rem;
  }

  strong { font-size: .82rem; }
  span { color: #655d54; font-size: .73rem; line-height: 1.45; }

  @media (max-width: 58rem) {
    grid-template-columns: auto minmax(0, 1fr);
  }

  @media (max-width: 35rem) {
    grid-template-columns: 1fr;
  }
`;

const AccessNoticeIcon = styled.div`
  display: grid;
  width: 2.45rem;
  height: 2.45rem;
  place-items: center;
  border-radius: .8rem;
  background: #111;
  color: #fff;

  svg { font-size: 1.15rem; }
`;

const AccessNoticeLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .45rem;

  button {
    min-height: 2.35rem;
    padding: .5rem .7rem;
    border: 1px solid #d3c3a9;
    border-radius: 999px;
    background: #fff;
    color: #4d4030;
    font: inherit;
    font-size: .68rem;
    font-weight: 850;
    cursor: pointer;
  }

  @media (max-width: 58rem) {
    grid-column: 2;
  }

  @media (max-width: 35rem) {
    grid-column: 1;
  }
`;

const DiscoveryGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(22rem, .92fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 64rem) { grid-template-columns: 1fr; }
`;

const MapCard = styled.div`
  position: sticky;
  top: 5.6rem;
  min-height: 38rem;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 1.25rem;
  background: #fff;

  > div:first-child { min-height: 38rem; }

  @media (max-width: 64rem) { position: relative; top: auto; }
  @media (max-width: 42rem) {
    min-height: 28rem;
    > div:first-child { min-height: 28rem; }
  }
`;

const MapLegend = styled.div`
  position:absolute;left:.8rem;top:.8rem;z-index:3;display:flex;flex-wrap:wrap;gap:.38rem;padding:.42rem;
  border:1px solid rgba(0,0,0,.08);border-radius:.75rem;background:rgba(255,255,255,.94);box-shadow:0 .35rem 1rem rgba(0,0,0,.09);backdrop-filter:blur(6px);
`;
const LegendItem = styled.span`
  display:inline-flex;align-items:center;gap:.28rem;color:#3f3a34;font-size:.58rem;font-weight:850;
  i{display:grid;place-items:center;width:1.25rem;height:1.25rem;border-radius:50%;background:${({$kind})=>$kind==='demand'?'#b1843d':$kind==='artisan'?'#111111':'#2f7a4d'};color:#fff;font-style:normal;font-size:.58rem;}
`;
const MapPrivacyNote = styled.div`
  position: absolute;
  left: .7rem;
  bottom: .7rem;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  padding: .5rem .65rem;
  border: 1px solid rgba(255, 255, 255, .75);
  border-radius: 999px;
  background: rgba(17, 17, 17, .88);
  color: #fff;
  font-size: .66rem;
  font-weight: 800;
  backdrop-filter: blur(8px);

  svg { font-size: .85rem; }
`;

const SideContent = styled.div`
  display: grid;
  gap: 1.7rem;
  min-width: 0;
`;

const List = styled.div`
  display: grid;
  gap: .75rem;
  min-width: 0;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: .8rem;

  > div { min-width: 0; }

  @media (max-width: 34rem) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.28rem;
  letter-spacing: -.02em;
`;

const SectionSubtitle = styled.p`
  margin: .2rem 0 0;
  color: var(--color-textMuted);
  font-size: .72rem;
  line-height: 1.45;
`;

const SectionPill = styled.span`
  flex: none;
  padding: .38rem .58rem;
  border-radius: 999px;
  background: #f1ece5;
  color: #69543a;
  font-size: .65rem;
  font-weight: 850;
  white-space: nowrap;
`;

const ArtisanCard = styled.article`
  display: grid;
  gap: .8rem;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 .35rem 1.2rem rgba(29, 24, 19, .035);
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;

  ${({ $interactive }) => $interactive && `
    &:hover { transform: translateY(-2px); border-color: #cdb993; box-shadow: 0 .75rem 1.8rem rgba(29, 24, 19, .08); }
    &:focus-visible { outline: 3px solid rgba(192,163,117,.35); outline-offset: 3px; }
  `}
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: .7rem;
  min-width: 0;

  img { width: 3.2rem; height: 3.2rem; border-radius: 50%; object-fit: cover; }
  div { display: grid; min-width: 0; }
  strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { display: flex; align-items: center; gap: .25rem; color: var(--color-textMuted); font-size: .72rem; }
  svg { font-size: .9rem; }
`;

const DemandCard = styled.article`
  display: grid;
  grid-template-columns: 8rem minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 .35rem 1.2rem rgba(29, 24, 19, .035);
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;

  ${({ $interactive }) => $interactive && `
    &:hover { transform: translateY(-2px); border-color: #cdb993; box-shadow: 0 .75rem 1.8rem rgba(29, 24, 19, .08); }
    &:focus-visible { outline: 3px solid rgba(192,163,117,.35); outline-offset: 3px; }
  `}

  @media (max-width: 32rem) { grid-template-columns: 1fr; }
`;

const DemandMedia = styled.div`
  position: relative;
  min-height: 100%;
  background: #f1eee8;

  img { width: 100%; height: 100%; min-height: 10rem; object-fit: cover; }
`;

const PreviewBadge = styled.span`
  position: absolute;
  left: .55rem;
  bottom: .55rem;
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .34rem .48rem;
  border-radius: 999px;
  background: rgba(17, 17, 17, .86);
  color: #fff;
  font-size: .61rem;
  font-weight: 850;
  backdrop-filter: blur(5px);

  svg { font-size: .75rem; }
`;

const DemandBody = styled.div`
  display: grid;
  gap: .58rem;
  min-width: 0;
  padding: .95rem;

  p {
    display: -webkit-box;
    overflow: hidden;
    margin: 0;
    color: #4f4a44;
    font-size: .78rem;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
`;

const DemandTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;

  span { padding: .3rem .5rem; border-radius: 999px; background: #eaf3fb; color: #0a4d7d; font-size: .68rem; font-weight: 850; }
  small { display: inline-flex; align-items: center; gap: .25rem; color: #726b61; font-size: .68rem; white-space: nowrap; }
  svg { font-size: .85rem; }
`;

const LocationLine = styled.span`
  display: flex;
  align-items: center;
  gap: .3rem;
  color: #6c665f;
  font-size: .72rem;

  svg { font-size: .9rem; }
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
`;

const PrimaryCardAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .35rem;
  min-height: 2.35rem;
  padding: .5rem .72rem;
  border: 0;
  border-radius: .75rem;
  background: #111;
  color: #fff;
  font: inherit;
  font-size: .69rem;
  font-weight: 850;
  cursor: pointer;

  svg { font-size: .9rem; }
`;

const SecondaryCardAction = styled.button`
  min-height: 2.35rem;
  padding: .5rem .72rem;
  border: 1px solid #d8d0c5;
  border-radius: .75rem;
  background: #fff;
  color: #111;
  font: inherit;
  font-size: .69rem;
  font-weight: 850;
  cursor: pointer;
`;

const ProjectsSection = styled.section`
  margin-top: 2.4rem;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .85rem;
  margin-top: .9rem;

  @media (max-width: 58rem) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 38rem) { grid-template-columns: 1fr; }
`;

const ProjectCard = styled.article`
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  background: #fff;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: transform .18s ease, box-shadow .18s ease;

  ${({ $interactive }) => $interactive && `
    &:hover { transform: translateY(-2px); box-shadow: 0 .75rem 1.8rem rgba(29, 24, 19, .08); }
    &:focus-visible { outline: 3px solid rgba(192,163,117,.35); outline-offset: 3px; }
  `}
  img { width: 100%; aspect-ratio: 16/10; object-fit: cover; }
  div { padding: .85rem; }
  span { display: flex; align-items: center; gap: .3rem; color: #745318; font-size: .68rem; font-weight: 850; }
  svg { font-size: .9rem; }
  h3 { margin: .45rem 0 .2rem; font-size: 1rem; }
  p { margin: 0; color: var(--color-textMuted); font-size: .72rem; }
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: 1rem;
  border: 1px dashed #d3ccc1;
  border-radius: 1rem;
  background: #fff;
  color: var(--color-textMuted);
  font-size: .8rem;
  line-height: 1.5;
`;

const FinalCta = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1.5rem;
  align-items: center;
  margin-top: 2.5rem;
  padding: clamp(1.25rem, 3vw, 2rem);
  border-radius: 1.35rem;
  background: #111;
  color: #fff;

  h2 {
    max-width: 42rem;
    margin: 0;
    color: #ffffff !important;
    font-size: clamp(1.45rem, 3vw, 2.25rem);
    line-height: 1.1;
    letter-spacing: -0.03em;
  }

  p {
    margin: 0.55rem 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 0.82rem;
  }

  @media (max-width: 58rem) {
    grid-template-columns: 1fr;
  }
`;

const FinalCtaActions = styled.div`
  display: grid;
  gap: .55rem;
  min-width: min(100%, 18rem);

  button, a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: .4rem;
    min-height: 2.8rem;
    padding: .65rem .85rem;
    border: 1px solid rgba(255, 255, 255, .22);
    border-radius: .8rem;
    background: #fff;
    color: #111;
    font: inherit;
    font-size: .74rem;
    font-weight: 850;
    text-decoration: none;
    cursor: pointer;
  }

  button:nth-child(2) { background: transparent; color: #fff; }
  a { min-height: auto; border: 0; background: transparent; color: rgba(255, 255, 255, .78); }
  svg { font-size: 1rem; }
`;

const GateBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  overflow-y: auto;
  padding: 1rem;
  background: rgba(16, 14, 12, .68);
  backdrop-filter: blur(8px);
`;

const GateDialog = styled.section`
  width: min(100%, 40rem);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  padding: clamp(1.2rem, 4vw, 2rem);
  border: 1px solid rgba(255, 255, 255, .55);
  border-radius: 1.5rem;
  background:
    radial-gradient(circle at 95% 4%, rgba(192, 163, 117, .24), transparent 27%),
    #fff;
  box-shadow: 0 2rem 6rem rgba(0, 0, 0, .28);

  h2 {
    max-width: 34rem;
    margin: 0;
    font-size: clamp(1.65rem, 4vw, 2.45rem);
    line-height: 1.05;
    letter-spacing: -.04em;
  }
`;

const GateTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const GateIcon = styled.span`
  display: grid;
  width: 2.8rem;
  height: 2.8rem;
  place-items: center;
  border-radius: .85rem;
  background: #111;
  color: #fff;

  svg { font-size: 1.25rem; }
`;

const CloseButton = styled.button`
  display: grid;
  width: 2.45rem;
  height: 2.45rem;
  place-items: center;
  border: 1px solid #ded7cd;
  border-radius: 50%;
  background: #fff;
  color: #111;
  cursor: pointer;
`;

const GateEyebrow = styled.span`
  display: block;
  margin-bottom: .5rem;
  color: #8d6c36;
  font-size: .69rem;
  font-weight: 900;
  letter-spacing: .1em;
  text-transform: uppercase;
`;

const GateDescription = styled.p`
  max-width: 35rem;
  margin: .8rem 0 0;
  color: #655f58;
  font-size: .86rem;
  line-height: 1.65;
`;

const BenefitList = styled.ul`
  display: grid;
  gap: .55rem;
  margin: 1.15rem 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: .45rem;
    color: #3f3a35;
    font-size: .78rem;
    line-height: 1.45;
  }

  svg { flex: none; margin-top: .05rem; color: #8d6c36; font-size: 1rem; }
`;

const RecommendedRole = styled.button`
  display: grid;
  grid-template-columns: 2.8rem minmax(0, 1fr) auto;
  gap: .8rem;
  align-items: center;
  width: 100%;
  padding: .85rem;
  border: 0;
  border-radius: 1rem;
  background: #111;
  color: #fff;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 .85rem 2rem rgba(17, 17, 17, .16);

  > span:nth-child(2) { display: grid; gap: .12rem; }
  small { color: #c0a375; font-size: .62rem; font-weight: 900; letter-spacing: .06em; text-transform: uppercase; }
  strong { font-size: .9rem; }
  em { color: rgba(255, 255, 255, .68); font-size: .67rem; font-style: normal; line-height: 1.35; }
  > svg:last-child { font-size: 1.1rem; }
`;

const RoleIcon = styled.span`
  display: grid;
  width: 2.8rem;
  height: 2.8rem;
  place-items: center;
  border-radius: .8rem;
  background: rgba(255, 255, 255, .1);

  svg { font-size: 1.25rem; }
`;

const AlternativeRole = styled.button`
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) auto;
  gap: .65rem;
  align-items: center;
  width: 100%;
  margin-top: .6rem;
  padding: .72rem .78rem;
  border: 1px solid #ded7cd;
  border-radius: .9rem;
  background: #fff;
  color: #111;
  text-align: left;
  cursor: pointer;

  > span { display: grid; gap: .1rem; }
  > svg { font-size: 1.05rem; }
  strong { font-size: .76rem; }
  small { color: #706960; font-size: .65rem; line-height: 1.35; }
`;

const GateLogin = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: .35rem;
  margin-top: 1rem;
  color: #706960;
  font-size: .72rem;

  button {
    border: 0;
    background: transparent;
    color: #111;
    font: inherit;
    font-weight: 900;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const GatePrivacy = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .3rem;
  margin: .8rem 0 0;
  color: #8a837a;
  font-size: .62rem;
  text-align: center;

  svg { font-size: .8rem; }
`;

const mapStateToProps = ({ firebase: firebaseState }) => ({
  loggedIn: Boolean(firebaseState.auth && firebaseState.auth.uid),
  emailVerified: Boolean(firebaseState.auth && firebaseState.auth.emailVerified),
  userType: firebaseState.profile && firebaseState.profile.user_type,
});

export default connect(mapStateToProps)(PublicDiscovery);
